/* eslint-disable jsx-a11y/no-static-element-interactions */
/* eslint-disable jsx-a11y/click-events-have-key-events */
import FormatBoldIcon from "@mui/icons-material/FormatBold";
import FormatItalicIcon from "@mui/icons-material/FormatItalic";
import FormatUnderlinedIcon from "@mui/icons-material/FormatUnderlined";
import IconButton from "@mui/material/IconButton";
import classnames from "classnames";
import { COLOR_STYLE_MAP, ColorControls } from "components/common/TextAreaeditorColors";
import { ContentState, Editor, EditorState, Modifier, RichUtils, convertFromHTML, convertToRaw } from "draft-js";
import { stateToHTML } from "draft-js-export-html";
import { stateFromHTML } from "draft-js-import-html";
import React, { forwardRef, useEffect, useImperativeHandle, useRef, useState } from "react";
import Immutable from "immutable";

const INLINE_STYLES = [
    {
        label: "bold",
        style: "BOLD",
    },
    {
        label: "italic",
        style: "ITALIC",
    },
    {
        label: "underline",
        style: "UNDERLINE",
    },
];

function getBlockStyle(block) {
    return block.getType() === "blockquote" ? "RichEditor-blockquote" : null;
}

const _ROOT_STYLES = {
    width: "100%",
    maxWidth: "99%",
};
function StyleIconButton({ readOnly, label, htmlStyle, active, onChangeStyle }) {
    function handleOnClick(e) {
        e.preventDefault();
        onChangeStyle(htmlStyle);
    }

    const _text = { fontSize: 18 };
    const opacity = readOnly ? {} : active ? { opacity: 1, color: "black" } : { opacity: 1, color: "darkgray" };
    const styles = Object.assign(_text, opacity);
    let icon = null;
    if (label === "bold") {
        icon = <FormatBoldIcon style={styles} />;
    } else if (label === "italic") {
        icon = <FormatItalicIcon style={styles} />;
    } else if (label === "underline") {
        icon = <FormatUnderlinedIcon style={styles} />;
    }
    return (
        <IconButton disabled={readOnly} size={"small"} onMouseDown={handleOnClick}>
            {icon}
        </IconButton>
    );
}

const EXPORT_OPTIONS = {
    inlineStyles: {
        BOLD: { element: "b" },
        UNDERLINE: { element: "u" },
        ITALIC: {
            element: "em",
        },

        red: {
            attributes: { "data-color": "red" },
            style: { color: COLOR_STYLE_MAP.red.color },
        },
        green: {
            attributes: { "data-color": "green" },
            style: { color: COLOR_STYLE_MAP.green.color },
        },
        yellow: {
            attributes: { "data-color": "yellow" },
            style: { color: COLOR_STYLE_MAP.yellow.color },
        },
    },
};

function findByColor(_color) {
    let color = null;
    Object.keys(COLOR_STYLE_MAP).map((k) => {
        const ob = COLOR_STYLE_MAP[k];
        if (ob.color === _color) {
            color = k;
        }
    });
    return color;
}

const IMPORT_OPTIONS = {
    customInlineFn: (element, { Style }) => {
        if (element.style.color) {
            let color = null;
            if (color) {
                return Style(color);
            } else {
                return Style(findByColor(element.style.color));
            }
        }
    },
};

export function editorHasContent(editorState) {
    const content = editorState.getCurrentContent();
    const raw = convertToRaw(content);
    return raw && raw.blocks && raw.blocks.length > 0;
}


const TextAreaEditor = forwardRef((props, ref) => {

    // The component instance will be extended
    // with whatever you return from the callback passed
    // as the second argument
    const {
        readOnly,
        placeholder,
        styles,
        rootStyle,
        className,
        defaultValue,
        reportChange,
        error,
        onChangeText
    } = props;


    const [editorState, setEditorState] = useState(EditorState.createEmpty());
    const [hasFocus] = useState(true);
    const [inlineStyles, setInlineStyles] = useState([]);
    const editorRef = useRef();
    const _styles = Object.assign({}, { maxHeight: 200, minHeight: 100 }, styles);
    const editable = !readOnly && hasFocus;
    const _classNames = classnames(className,  " text-editor-root ", editable && "text-editor-root-editable", error && "ats-autoTextareaError" );

    useImperativeHandle(ref, () => ({
        getValue(plainText) {
            const content = editorState.getCurrentContent();
            const html = stateToHTML(content, EXPORT_OPTIONS);
            const raw = convertToRaw(content);
            if (plainText && raw && raw.blocks) {
                return raw.blocks.map((b) => b.text).join(" ");
            } else {
                return html !== "<p><br></p>" ? html : "";
            }
        },
        setValue(value) {
            _setValue(value);
        },
    }));

    function _setValue(value) {
        if (editorRef.current) {
            const blocksFromHTML = convertFromHTML(value);
            if (!blocksFromHTML.contentBlocks) {
                setEditorState(EditorState.createWithContent(ContentState.createFromText("")));
            } else {
                const contentState = stateFromHTML(value, IMPORT_OPTIONS);

                const editorS = EditorState.createWithContent(contentState);
                setEditorState(editorS);
            }
            const currentStyle = editorState.getCurrentInlineStyle();
            const _inlineStyles = [];
            INLINE_STYLES.map((type) => {
                if (currentStyle.has(type.style)) {
                    _inlineStyles.push(type.style);
                }
            });
            setInlineStyles(_inlineStyles);
        }
    }

    useEffect(() => {
        _setValue(defaultValue);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [defaultValue]);

    function handleKeyCommand(command) {
        const newState = RichUtils.handleKeyCommand(editorState, command);
        if (newState) {
            onChange(newState);
            return true;
        }
        return false;
    }


    function onChange(_editorState) {
        setEditorState(_editorState);
        if(reportChange)
            reportChange();
        if(onChangeText) {
            onChangeText();
        }
    }

    function onChangeStyle(inlineStyle) {
        let _inlineStyles = [...inlineStyles];
        const index = inlineStyles.findIndex((st) => st === inlineStyle);
        if (index === -1) {
            _inlineStyles.push(inlineStyle);
        } else {
            _inlineStyles = inlineStyles.filter((_, i) => i !== index);
        }
        setInlineStyles(_inlineStyles);
        onChange(RichUtils.toggleInlineStyle(editorState, inlineStyle));
    }

    function handleOnBlur() {}
    function handleOnFocus() {}
    function rootOnClick() {
        editorRef.current.focus();
    }

    function handlePastedText(html, styles, editorState) {
        if (html) {
            let blocks = convertFromHTML(html);
            let linkRemoved = false;
            const map = Immutable.Map(COLOR_STYLE_MAP, INLINE_STYLES);
            let pastedState = ContentState.createFromBlockArray(
                blocks.contentBlocks,
                map
            );
            blocks.contentBlocks.forEach(contentBlock =>{
                contentBlock.findEntityRanges(
                    (character) => {
                        const entityKey = character.getEntity();
                        return (
                            entityKey !== null &&
                                pastedState.getEntity(entityKey).getType() === "LINK"
                        );
                    },
                    (start) => {
                        pastedState = pastedState.replaceEntityData(contentBlock.getEntityAt(start),{});
                        linkRemoved = true;
                    }
                );
            }
            );
            if (linkRemoved) {
                const modifiedContent = Modifier.replaceWithFragment(editorState.getCurrentContent(),
                    editorState.getSelection(),
                    pastedState.getBlockMap()
                );

                const newEditorState = EditorState.push(editorState, modifiedContent, "insert-fragment");
                setEditorState(newEditorState);
            }
            return linkRemoved;
        }

    }


    function toggleColor(toggledColor) {
        const selection = editorState.getSelection();

        const nextContentState = Object.keys(COLOR_STYLE_MAP).reduce((contentState, color) => {
            return Modifier.removeInlineStyle(contentState, selection, color);
        }, editorState.getCurrentContent());

        let nextEditorState = EditorState.push(editorState, nextContentState, "change-inline-style");

        const currentStyle = editorState.getCurrentInlineStyle();

        if (selection.isCollapsed()) {
            nextEditorState = currentStyle.reduce((state, color) => {
                return RichUtils.toggleInlineStyle(state, color);
            }, nextEditorState);
        }

        if (!currentStyle.has(toggledColor)) {
            nextEditorState = RichUtils.toggleInlineStyle(nextEditorState, toggledColor);
        }

        onChange(nextEditorState);
    }

    const btnStyles = INLINE_STYLES.map((type) => {
        const isActive = inlineStyles.find((st) => st === type.style);
        return (
            <StyleIconButton
                key={type.label}
                readOnly={readOnly}
                onChangeStyle={onChangeStyle}
                htmlStyle={type.style}
                active={isActive}
                label={type.label}
            />
        );
    });
    const _rootStyle = Object.assign({}, _ROOT_STYLES, rootStyle);
    return (
        <div style={_rootStyle} onBlur={handleOnBlur} onClick={handleOnFocus}>
            {editable && (
                <div className="text-editor-controls-container">
                    {btnStyles}

                    <ColorControls readOnly={readOnly} editorState={editorState} onToggle={toggleColor} />
                </div>
            )}
            <div className={_classNames} style={_styles} onClick={rootOnClick}>
                <Editor
                    customStyleMap={COLOR_STYLE_MAP}
                    ref={editorRef}
                    blockStyleFn={getBlockStyle}
                    editorState={editorState}
                    readOnly={readOnly}
                    handlePastedText={handlePastedText}
                    handleKeyCommand={handleKeyCommand}
                    onChange={onChange}
                    placeholder={placeholder}
                    spellCheck={true}
                />
            </div>
        </div>
    );
});

export default TextAreaEditor;
